import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { MapPin, Clock, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const KIND_COLORS = { WALK: "bg-blue", RIDE: "bg-yellow text-ink", SKATE: "bg-coral", HANG: "bg-navy", "POP-UP": "bg-red" };

const formatDate = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return {
    month: d.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
    day: d.getDate(),
    weekday: d.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase(),
  };
};

export default function ShowUp() {
  const [gatherings, setGatherings] = useState([]);
  const [rsvpFor, setRsvpFor] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.gatherings().then(setGatherings).catch(() => {});
  }, []);

  const submitRsvp = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await api.rsvp(rsvpFor.id, { name: name.trim(), email: email.trim() });
      setGatherings((prev) => prev.map((g) => (g.id === rsvpFor.id ? { ...g, rsvp_count: res.rsvp_count } : g)));
      toast.success(`YOU'RE IN — ${rsvpFor.title.toUpperCase()}`);
      setRsvpFor(null);
      setName("");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 pt-14 relative" data-testid="showup-page">
      <img
        src="/assets/mascot-lean.png"
        alt="RUNRS letterman mascot"
        data-testid="showup-mascot"
        className="absolute top-4 right-4 w-24 sm:w-40 lg:w-56 -rotate-2 pointer-events-none select-none"
      />
      <h1 className="font-college text-5xl sm:text-6xl text-navy max-w-[65%] sm:max-w-none" data-testid="showup-heading">SHOW UP</h1>
      <p className="font-college text-xl md:text-2xl text-red mt-3" data-testid="showup-subline">
        Walk. Run. Ride. Move. Participate.
      </p>
      <p className="text-ink/70 mt-4 max-w-lg text-base md:text-lg leading-relaxed">
        Walks, rides, skate nights, pop-ups, food, releases.
        There is no pace requirement and no finish line. Being there is the whole idea.
      </p>

      <div className="mt-14 space-y-0 border-t border-ink/15" data-testid="gatherings-list">
        {gatherings.map((g) => {
          if (g.tba) {
            return (
              <article
                key={g.id}
                data-testid={`gathering-${g.id}`}
                className="grid gap-5 md:grid-cols-12 items-center border-b border-ink/15 py-8"
              >
                <div className="md:col-span-2">
                  <p className="font-college text-lg text-red">DETAILS</p>
                  <p className="font-college text-5xl text-navy leading-none" data-testid={`gathering-tba-${g.id}`}>TBA</p>
                </div>
                <div className="md:col-span-2">
                  <div className="w-28 h-20 bg-white shadow-print-sm flex items-center justify-center">
                    <img src="/assets/lowkey-logo.webp" alt="LowKey Skateshop" className="w-full h-full object-contain p-2" />
                  </div>
                </div>
                <div className="md:col-span-5">
                  <h2 className="font-college text-2xl text-navy leading-tight" data-testid={`gathering-title-${g.id}`}>{g.title}</h2>
                  <a
                    href="https://www.lowkeysanfrancisco.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="visit-lowkey-link"
                    className="inline-block mt-2 font-college text-sm text-navy/70 underline underline-offset-4 hover:text-red transition-colors"
                  >
                    VISIT LOWKEY ↗
                  </a>
                </div>
                <div className="md:col-span-3 md:text-right space-y-2">
                  <p className="text-sm text-ink/70 flex md:justify-end items-center gap-1.5">
                    <MapPin size={14} /> {g.location}
                  </p>
                  <p className="text-sm text-ink/70 flex md:justify-end">{g.city}</p>
                </div>
              </article>
            );
          }
          const d = formatDate(g.date);
          return (
            <article
              key={g.id}
              data-testid={`gathering-${g.id}`}
              className="grid gap-5 md:grid-cols-12 items-center border-b border-ink/15 py-8"
            >
              <div className="md:col-span-2 flex md:block items-baseline gap-3">
                <p className="font-college text-lg text-red">{d.month}</p>
                <p className="font-college text-5xl text-navy leading-none" data-testid={`gathering-day-${g.id}`}>{d.day}</p>
                <p className="text-xs text-ink/50 mt-1">{d.weekday}</p>
              </div>
              <div className="md:col-span-2">
                <div className="w-24 h-24 bg-white shadow-print-sm">
                  <img src={g.image} alt="" className="w-full h-full object-contain p-2" />
                </div>
              </div>
              <div className="md:col-span-5">
                <span className={`inline-block font-college text-xs px-2 py-0.5 text-paper ${KIND_COLORS[g.kind] || "bg-navy"}`}>
                  {g.kind}
                </span>
                <h2 className="font-college text-2xl text-navy mt-2 leading-tight" data-testid={`gathering-title-${g.id}`}>{g.title}</h2>
                <p className="text-sm text-ink/70 mt-2 max-w-md leading-relaxed">{g.description}</p>
              </div>
              <div className="md:col-span-3 md:text-right space-y-2">
                <p className="text-sm text-ink/70 flex md:justify-end items-center gap-1.5">
                  <Clock size={14} /> {g.time}
                </p>
                <p className="text-sm text-ink/70 flex md:justify-end items-center gap-1.5">
                  <MapPin size={14} /> {g.location}
                </p>
                <p className="text-xs text-ink/50 flex md:justify-end items-center gap-1.5">
                  <Users size={13} /> <span data-testid={`rsvp-count-${g.id}`}>{g.rsvp_count} showing up</span>
                </p>
                {g.rsvp_enabled && (
                  <button
                    data-testid={`rsvp-button-${g.id}`}
                    onClick={() => setRsvpFor(g)}
                    className="bg-navy text-paper font-college text-lg px-5 py-2 shadow-print-sm hover:bg-red transition-colors"
                  >
                    I'LL BE THERE
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <Dialog open={!!rsvpFor} onOpenChange={(o) => !o && setRsvpFor(null)}>
        <DialogContent className="bg-paper rounded-none shadow-print sm:max-w-md" data-testid="rsvp-dialog">
          <DialogHeader>
            <DialogTitle className="font-college text-2xl text-navy">
              RSVP — {rsvpFor?.title.toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-ink/60">{rsvpFor?.date} · {rsvpFor?.time} · {rsvpFor?.location}</p>
          <form onSubmit={submitRsvp} className="space-y-3 mt-2">
            <input
              data-testid="rsvp-name-input"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red"
            />
            <input
              data-testid="rsvp-email-input"
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red"
            />
            <button
              data-testid="rsvp-submit-button"
              type="submit"
              disabled={sending}
              className="w-full bg-red text-paper font-college text-xl py-3 shadow-print-sm hover:bg-navy transition-colors disabled:opacity-60"
            >
              {sending ? "HOLD ON…" : "COUNT ME IN"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
